import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { ModelInfo } from '@/types';

interface UseModelsOptions {
  tier?: string;
}

interface UseModelsReturn {
  models: ModelInfo[];
  byTier: Record<string, ModelInfo[]>;
  providers: string[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useModels(options: UseModelsOptions = {}): UseModelsReturn {
  const { tier } = options;
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [byTier, setByTier] = useState<Record<string, ModelInfo[]>>({});
  const [providers, setProviders] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchModels = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.getModels(tier);
      setModels(data.models);
      setByTier(data.by_tier);
      setProviders(data.providers);
      setTotalCount(data.total_count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setIsLoading(false);
    }
  }, [tier]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  return { models, byTier, providers, totalCount, isLoading, error, refresh: fetchModels };
}

